/**
 * Maps a value within a custom range to Tailwind color classes
 * @param {number} value - Current value to map
 * @param {number} min - Lower bound of the range
 * @param {number} max - Upper bound of the range
 * @param {boolean} isHigherBetter - Determines if higher values are better
 * @param {boolean} useBorder - Determines if the border should be used
 * @returns {object} Tailwind color classes
 */

import colors from "@/config/colors";

/**
 *
 */
export interface ColorClass {
  background: string;
  text: string;
  border: string;
}

const DEFAULT_THRESHOLD = 30;
const INTENSITY_RANGE = 600;
const START_COLOR = [215, 108, 31] as const;
const END_COLOR = [42, 121, 72] as const;
const DARK_MID_COLOR = [0, 0, 0] as const;
const LIGHT_MID_COLOR = [255, 255, 255] as const;

const interpolateColorValue = (start: number, end: number, factor: number) =>
  Math.round(start + (end - start) * factor);

const interpolateColor = (intensity: number, isDarkMode: boolean): string => {
  const midColor = isDarkMode ? DARK_MID_COLOR : LIGHT_MID_COLOR;
  const factor = intensity / INTENSITY_RANGE;

  const [r, g, b] =
    factor <= 0.5
      ? [
          interpolateColorValue(START_COLOR[0], midColor[0], factor * 2),
          interpolateColorValue(START_COLOR[1], midColor[1], factor * 2),
          interpolateColorValue(START_COLOR[2], midColor[2], factor * 2),
        ]
      : [
          interpolateColorValue(midColor[0], END_COLOR[0], (factor - 0.5) * 2),
          interpolateColorValue(midColor[1], END_COLOR[1], (factor - 0.5) * 2),
          interpolateColorValue(midColor[2], END_COLOR[2], (factor - 0.5) * 2),
        ];

  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

const isTooClose = (
  hex: string,
  isDarkMode: boolean,
  threshold: number = DEFAULT_THRESHOLD
): boolean => {
  const color = parseInt(hex.slice(1), 16);
  const r = (color >> 16) & 255;
  const g = (color >> 8) & 255;
  const b = color & 255;
  const brightness = (r + g + b) / 3;

  return isDarkMode ? brightness < threshold : brightness > 255 - threshold;
};

const calculateLuminance = (channel: number): number => {
  const sRGB = channel / 255;
  return sRGB <= 0.03928 ? sRGB / 12.92 : Math.pow((sRGB + 0.055) / 1.055, 2.4);
};

const getTextColor = (
  backgroundHex: string,
  isTransparent: boolean,
  isDarkMode: boolean
): string => {
  if (isTransparent) {
    return isDarkMode ? "#FFFFFF" : "#000000";
  }

  const color = parseInt(backgroundHex.slice(1), 16);
  const r = (color >> 16) & 255;
  const g = (color >> 8) & 255;
  const b = color & 255;

  const luminance =
    0.2126 * calculateLuminance(r) +
    0.7152 * calculateLuminance(g) +
    0.0722 * calculateLuminance(b);

  return luminance > 0.5 ? "#000000" : "#FFFFFF";
};

const getBorderColor = (
  colorHex: string,
  useBorder: boolean,
  isDarkMode: boolean
): string => {
  const defaultBorder = isDarkMode
    ? colors.statusBadge.default.border[500]
    : "#D4D4D8";

  if (!useBorder) return defaultBorder;

  return isTooClose(colorHex, isDarkMode) ? defaultBorder : colorHex;
};

const getBackgroundColor = (
  colorHex: string,
  useBorder: boolean,
  isDarkMode: boolean
): string => {
  if (!useBorder) return "transparent";

  return isDarkMode ? `${colorHex}80` : colorHex;
};

const normalizeValue = (value: number, min: number, max: number): number => {
  if (min === max) return 50;

  const percentage = ((value - min) / (max - min)) * 100;
  return Math.max(0, Math.min(100, percentage));
};

const getColorClass = (
  value: number | undefined | null,
  min: number,
  max: number,
  isHigherBetter: boolean = true,
  useBorder: boolean = true,
  isDarkMode: boolean = true
): ColorClass => {
  if (value === undefined || value === null) {
    return {
      background: isDarkMode
        ? colors.statusBadge.default.background[500]
        : "#F4F4F5",
      text: isDarkMode ? colors.statusBadge.default.text[500] : "#3F3F46",
      border: isDarkMode ? colors.statusBadge.default.border[500] : "#D4D4D8",
    };
  }

  const normalizedValue = normalizeValue(value, min, max);
  const adjustedValue = isHigherBetter
    ? normalizedValue
    : 100 - normalizedValue;
  const intensity = Math.round((adjustedValue / 100) * INTENSITY_RANGE);
  const colorHex = interpolateColor(intensity, isDarkMode);
  const backgroundColor = getBackgroundColor(colorHex, useBorder, isDarkMode);
  const isTransparentBackground = backgroundColor === "transparent";

  return {
    background: backgroundColor,
    text: getTextColor(colorHex, isTransparentBackground, isDarkMode),
    border: getBorderColor(colorHex, useBorder, isDarkMode),
  };
};

export default getColorClass;
